import { cn, createBEM } from "@/libs";
import { ReactNode, createContext, useContext } from "react";
import { create } from "zustand";
import "./index.scss";
import GlobalEvent from '@/utils/globalEvent';

interface ShowProps {
  className?: string;
  maskClassName?: string;
  content?: ReactNode;
  closeOnClick?: boolean;
}
export type ShowResult = {
  close: () => void;
};
type Show = (props: {
  className?: string;
  maskClassName?: string;
  content?: ReactNode;
  closeOnClick?: boolean;
}) => ShowResult;
type PopupContextType = [show: Show];
export const PopupContext = createContext<PopupContextType | null>(null);

interface PopupContextState {
  active: boolean;
  className?: string;
  maskClassName?: string;
  content?: ReactNode;
  closeOnClick?: boolean;
  showPopup: (props: ShowProps) => void;
  closePopup: () => void;
}
// PopupProvider 中使用，提供该组件的唯一状态，所以该组件是单例的
const usePopupContextStore = create<PopupContextState>((set) => ({
  active: false,
  className: undefined,
  maskClassName: undefined,
  content: "",
  closeOnClick: false,
  showPopup: (props: ShowProps) => set(() => ({ active: true, ...props })),
  closePopup: () =>
    set(() => ({
      active: false,
      className: undefined,
      maskClassName: undefined,
      content: "",
      closeOnClick: false,
    })),
}));

const b2 = createBEM("i-popup");
export const PopupProvider = ({ children }: { children: ReactNode }) => {
  const globalEvent = GlobalEvent.getInstance();
  const [
    active,
    className,
    maskClassName,
    content,
    showPopup,
    closePopup,
    closeOnClick,
  ] = usePopupContextStore((state) => [
    state.active,
    state.className,
    state.maskClassName,
    state.content,
    state.showPopup,
    state.closePopup,
    state.closeOnClick,
  ]);

  const show: Show = (props) => {
    if (!active) {
      showPopup(props);
    }

    return {
      close() {
        closePopup();
      },
    };
  };

  const component = (
    <>
      <div
        className={cn(b2("mask"), maskClassName)}
        onClick={() => {
          closeOnClick && closePopup();
          globalEvent.submit('closePopup');
        }}
      />
      <div className={cn(b2(), className)}>{content}</div>
    </>
  );

  return (
    <PopupContext.Provider value={[show]}>
      <>
        {children}
        {active && component}
      </>
    </PopupContext.Provider>
  );
};

/**
 * andt-mobile 本身 Dialog 组件的样式权重太高，覆盖起来太麻烦
 * 如出现报错请在回调中使用，不可直接用于函数组件同步代码中
 */
export const usePopup = () => {
  return useContext(PopupContext) as PopupContextType;
};
